import classnames from 'classnames'
import styles from './ProductGalleryNav.module.scss'
import useStore from '@/store'
import { getGalleryTopOffset } from '@/sections/ProductGallery/ProductGallery'
import { useEffect, useMemo, useRef, useState } from 'react'
import gsap from 'gsap'
import { formatNumber } from '@/utils'
import useWindowResize from '@/hooks/use-window-resize'
import { TRANSITION_DURATION } from '@/data'
import useCurrentPage from '@/hooks/use-current-page'

const ProductGalleryNav = () => {
  const setProductGalleryType = useStore(state => state.setProductGalleryType)
  const productGalleryType = useStore(state => state.productGalleryType)
  const productGalleryTransitioning = useStore(state => state.productGalleryTransitioning)
  const productGalleryProducts = useStore(state => state.productGalleryProducts)
  const productGalleryFilters = useStore(state => state.productGalleryFilters)
  const [galleryTopOffset, setGalleryTopOffset] = useState(0)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const setProductGalleryActiveFilter = useStore(state => state.setProductGalleryActiveFilter)
  const productGalleryActiveFilter = useStore(state => state.productGalleryActiveFilter)
  const productGalleryActiveProductData = useStore(state => state.productGalleryActiveProductData)
  const $filterButtons = useRef([])
  const $filterIcon = useRef(null)
  const $filterButtonContainers = useRef([])
  const resizeKey = useWindowResize()
  const isActiveDebounceTimeout = useRef(null)
  const { isProductsPage } = useCurrentPage()
  const $themeContainer = useRef(null)
  const theme = useStore(state => state.theme)
  const $allProductTextContainer = useRef(null)
  const setTheme = useStore(state => state.setTheme)
  const $buttons = useRef({
    list: null,
    grid: null,
  })
  const $buttonsContainer = useRef(null)
  const $bottom = useRef(null)
  const $filtersContainer = useRef(null)
  const [listViewActiveProductData, setListViewActiveProductData] = useState({})

  const filters = useMemo(() => {
    if (!productGalleryFilters?.length) return []
    return productGalleryFilters.map(filter => filter.title)
  }, [productGalleryFilters])

  useEffect(() => {
    if (productGalleryType === 'list') {
      setListViewActiveProductData(productGalleryActiveProductData)
    }
  }, [productGalleryType, productGalleryActiveProductData])

  useEffect(() => {
    if (!isProductsPage) {
      setProductGalleryActiveFilter(null)
      setFiltersOpen(false)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductsPage])

  useEffect(() => {
    if (isActiveDebounceTimeout.current) {
      clearTimeout(isActiveDebounceTimeout.current)
    }

    if (!filters?.length) return

    isActiveDebounceTimeout.current = setTimeout(() => {
      const items = [
        $buttonsContainer.current,
        $filtersContainer.current,
        $themeContainer.current,
        $allProductTextContainer.current,
      ]

      gsap.killTweensOf(items)

      gsap.set(items, {
        pointerEvents: isProductsPage ? 'auto' : 'none',
      })

      gsap.to(items, {
        autoAlpha: isProductsPage ? 1 : 0,
        duration: 0.8,
        delay: isProductsPage ? TRANSITION_DURATION * 0.75 : 0,
      })
    }, 50)
  }, [filters?.length, isProductsPage])

  useEffect(() => {
    if (!isProductsPage) {
      gsap.to($bottom.current, {
        autoAlpha: 0,
        duration: 0.3,
      })
      return
    }
    const bottomIsActive = productGalleryType === 'list'
    gsap.to($bottom.current, {
      autoAlpha: bottomIsActive ? 1 : 0,
      duration: 0.4,
    })
  }, [isProductsPage, productGalleryType])

  useEffect(() => {
    const galleryTopOffset = getGalleryTopOffset()
    setGalleryTopOffset(galleryTopOffset)
  }, [resizeKey])

  useEffect(() => {
    if (!$filterButtonContainers.current || !$filterIcon.current) return
    $filterButtonContainers.current.forEach((button, i) => {
      gsap.killTweensOf(button)
      gsap.to(button, {
        opacity: filtersOpen ? 1 : 0,
        duration: 0.4,
        delay: filtersOpen ? i * 0.1 : 0,
        ease: 'power2.inOut',
        pointerEvents: filtersOpen ? 'auto' : 'none',
      })
    })

    gsap.killTweensOf($filterIcon.current)
    gsap.to($filterIcon.current, {
      rotate: filtersOpen ? 45 : 0,
      duration: 0.4,
    })
  }, [filtersOpen])

  const buttonClickHandler = type => {
    if (productGalleryTransitioning) return

    setFiltersOpen(false)

    if (productGalleryActiveFilter) {
      setProductGalleryActiveFilter(null)

      setTimeout(() => {
        setProductGalleryType(type)
      }, 200)
    } else {
      setProductGalleryType(type)
    }
  }

  if (!filters?.length) return null

  return (
    <>
      <div
        className={styles.themeContainer}
        ref={$themeContainer}
        data-theme={theme}
      >
        <button
          className={classnames(styles.themeButton, {
            [styles.isActive]: theme === 'light',
          })}
          aria-label="Change to white theme"
          onClick={() => {
            setTheme('light')
          }}
        >
          (w.)
        </button>
        <button
          className={classnames(styles.themeButton, {
            [styles.isActive]: theme === 'dark',
          })}
          aria-label="Change to red theme"
          onClick={() => {
            setTheme('dark')
          }}
        >
          (r.)
        </button>
      </div>
      <div
        ref={$filtersContainer}
        className={styles.filtersContainer}
        style={{
          '--height': `${galleryTopOffset}px`,
        }}
      >
        <button
          onClick={() => {
            setFiltersOpen(prev => {
              const newState = !prev
              if (newState === false) {
                setProductGalleryActiveFilter(null)
              }
              return newState
            })
          }}
          className={styles.filterToggleButton}
        >
          <span className={styles.filterToggleButton__label}>Filter</span>
          <span
            className={styles.filterToggleButton__icon}
            ref={$filterIcon}
          >
            +
          </span>
        </button>
        <ul className={styles.filters}>
          <li
            key={'allFiltr'}
            className={classnames(styles.filter, { [styles.isActive]: productGalleryActiveFilter === null })}
            ref={ref => {
              $filterButtons.current[0] = ref
            }}
          >
            <div
              className={styles.filter__buttonContainer}
              ref={ref => {
                $filterButtonContainers.current[0] = ref
              }}
            >
              <button
                className={styles.filter__button}
                aria-label={'Remove all filters'}
                onClick={() => {
                  setProductGalleryActiveFilter(null)
                }}
              >
                All
                <span className={styles.filter__buttonComma}>,</span>
              </button>
            </div>
          </li>
          {filters.map((filter, i) => {
            return (
              <li
                key={i}
                className={classnames(styles.filter, { [styles.isActive]: productGalleryActiveFilter === filter })}
                ref={ref => {
                  $filterButtons.current[i + 1] = ref
                }}
              >
                <div
                  className={styles.filter__buttonContainer}
                  ref={ref => {
                    $filterButtonContainers.current[i + 1] = ref
                  }}
                >
                  <button
                    className={styles.filter__button}
                    aria-label={`filter products to category ${filter}`}
                    onClick={() => {
                      if (filter === productGalleryActiveFilter) {
                        setProductGalleryActiveFilter(null)
                      } else {
                        setProductGalleryActiveFilter(filter)
                      }
                    }}
                  >
                    {filter}
                    {i !== filters.length - 1 && <span className={styles.filter__buttonComma}>,</span>}
                  </button>
                </div>
              </li>
            )
          })}
        </ul>
      </div>
      <div
        className={styles.toggleButtons}
        style={{
          '--height': `${galleryTopOffset}px`,
        }}
        ref={ref => {
          $buttonsContainer.current = ref
        }}
      >
        <button
          onClick={() => {
            buttonClickHandler('list')
          }}
          className={classnames(styles.button, styles.list, {
            [styles.isActive]: productGalleryType === 'list',
          })}
          ref={ref => {
            $buttons.current.list = ref
          }}
        >
          Feed
        </button>
        <span className={styles.toggleButtons__separator}>|</span>
        <button
          onClick={() => {
            buttonClickHandler('grid')
          }}
          className={classnames(styles.button, styles.grid, {
            [styles.isActive]: productGalleryType === 'grid',
          })}
          ref={ref => {
            $buttons.current.grid = ref
          }}
        >
          Grid
        </button>
      </div>
      <div
        style={{
          '--height': `${galleryTopOffset}px`,
        }}
        className={styles.allProductTextContainer}
        ref={ref => {
          $allProductTextContainer.current = ref
        }}
      >
        <p className={styles.allProductTextContainer__count}>({formatNumber(productGalleryProducts?.length, 2)})</p>
        <p className={styles.allProductTextContainer__text}>All Products</p>
      </div>
      <div
        style={{
          '--height': `${galleryTopOffset}px`,
        }}
        className={styles.bottom}
        ref={ref => {
          $bottom.current = ref
        }}
      >
        <div className={styles.productInfo}>
          <div className={styles.productInfo__index}>({formatNumber(listViewActiveProductData?.index + 1, 2)})</div>
          <div className={styles.productInfo__title}>{listViewActiveProductData?.title}</div>
        </div>
        <div className={styles.scrollText}>Scroll</div>
      </div>
    </>
  )
}

ProductGalleryNav.displayName = 'ProductGalleryNav'

export default ProductGalleryNav
